import { fetchBaseCurrencyChange, useFeesContext } from 'sonata-platform-components';
import { useQueryFetch } from 'sonata-react-components';

import { useGlobalStore } from '@/store/global';
import { getBaseCurrency } from '@/store/global/baseCurrency/selectors';
import { filterDealsByBidFloor, getDealsCurrencies, getDealsMaxBidPrice } from './dealsBidFloor.helpers';

import type { LineItemGoal } from 'sonata-static-storage/lib/entities';
import type { PricingModelConfig } from 'sonata-static-storage/lib/schemas/campaign/pricingModel.schema';
import type { DealInterface } from 'sonata-static-storage/lib/schemas/premium/deal.schema';
import type { CurrencyChanges } from './dealsBidFloor.helpers';

/**
 * Hides the deals whose bid floor (in the user currency) exceeds the entity max premium CPM after fees.
 * Without `pricingModelConfig` the deals are returned untouched.
 */
export function useDealsBidFloorFilter(
  deals: DealInterface[] | undefined,
  pricingModelConfig?: PricingModelConfig,
  goals: LineItemGoal[] = []
) {
  const token: Record<string, unknown> = {};
  const { totalFees } = useFeesContext();
  const userCurrency = useGlobalStore(getBaseCurrency).baseCurrency;
  const currencies = getDealsCurrencies(deals ?? [], userCurrency);

  const { data: currencyChanges, isLoading } = useQueryFetch<CurrencyChanges>(
    ['dealsCurrencyChanges', ...currencies],
    async () =>
      Object.fromEntries(
        await Promise.all(currencies.map(async currency => [currency, await fetchBaseCurrencyChange(currency, token)]))
      ),
    { enabled: !!pricingModelConfig && !!deals }
  );

  if (!pricingModelConfig || !deals) {
    return { deals, isLoading: false };
  }
  if (!currencyChanges) {
    return { deals: undefined, isLoading };
  }
  return {
    deals: filterDealsByBidFloor({
      currencyChanges,
      deals,
      maxBidPrice: getDealsMaxBidPrice(pricingModelConfig, totalFees, goals),
      userCurrency
    }),
    isLoading: false
  };
}
